import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";

import { useApNames } from "../api/use-ap-names";
import { useCreateBundle } from "../api/use-create-bundle";
import { useCreateSKU } from "../api/use-create-sku";
import {
  createSkuBundleSchema,
  type CreateSkuBundleFormInput,
  type CreateSkuBundleFormValues,
} from "../lib/create-sku-bundle-schema";

import { BaseProductPicker } from "./BaseProductPicker";

interface CreateSkuOrBundleModalProps {
  onClose: () => void;
  /** Preselect the "Is Bundle" toggle (used by the Bundles-tab create button). */
  defaultBundle?: boolean;
}

const FIELD_CLASS =
  "h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";

export function CreateSkuOrBundleModal({
  onClose,
  defaultBundle = false,
}: CreateSkuOrBundleModalProps): JSX.Element {
  const { t } = useTranslation("inventory");
  const createSku = useCreateSKU();
  const createBundle = useCreateBundle();
  const apNames = useApNames();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateSkuBundleFormInput, unknown, CreateSkuBundleFormValues>({
    resolver: zodResolver(createSkuBundleSchema),
    defaultValues: {
      is_bundle: defaultBundle,
      code: "",
      product_name: "",
      components: [],
      ap_name: "",
    },
  });

  const isBundle = useWatch({ control, name: "is_bundle" });
  const { fields, append, remove } = useFieldArray({ control, name: "components" });
  const pickedSkus = fields.map((f) => f.base_sino_sku);

  const isPending = createSku.isPending || createBundle.isPending;
  const serverError = createSku.error ?? createBundle.error;

  function onSubmit(values: CreateSkuBundleFormValues): void {
    if (values.is_bundle) {
      createBundle.mutate(
        {
          listing_code: values.code,
          product_name: values.product_name,
          components: values.components.map((c) => ({
            base_sino_sku: c.base_sino_sku,
            multiplicity: c.multiplicity,
          })),
        },
        { onSuccess: () => onClose() },
      );
      return;
    }
    // sino parity: no gift-stock pool. Every new base SKU is a plain SKU.
    const apName = values.ap_name?.trim();
    createSku.mutate(
      {
        sino_sku: values.code,
        product_name: values.product_name,
        ...(apName ? { ap_name: apName } : {}),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("modal.create_sku_bundle.title")}
      data-testid="create-sku-bundle-modal"
    >
      <button
        type="button"
        aria-label={t("modal.create_sku_bundle.action_cancel")}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{ background: "rgba(16,24,40,.28)", backdropFilter: "blur(2px)" }}
      />
      <div
        className="relative max-h-[88vh] w-full max-w-[480px] overflow-y-auto rounded-2xl bg-white"
        style={{ boxShadow: "var(--sh-modal)" }}
      >
        <h2 className="px-6 pt-5 text-[17px] font-extrabold tracking-tight text-[var(--text-primary)]">
          {t("modal.create_sku_bundle.title")}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 px-6 pb-6 pt-4">
          {/* Is Bundle toggle */}
          <label className="flex items-center justify-between gap-3 rounded-[var(--control-r)] border border-[var(--premium-border)] px-3 py-2.5">
            <span className="text-sm font-semibold text-[var(--text-primary)]">
              {t("modal.create_sku_bundle.is_bundle")}
            </span>
            <input
              type="checkbox"
              role="switch"
              data-testid="create-is-bundle-toggle"
              disabled={isPending}
              className="h-[18px] w-[18px]"
              style={{ accentColor: "var(--brand-500)" }}
              {...register("is_bundle")}
            />
          </label>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]">
              {t("modal.create_sku_bundle.field_name")}
            </label>
            <input
              type="text"
              data-testid="create-sku-bundle-product-name"
              disabled={isPending}
              placeholder={t("modal.create_sku_bundle.placeholder_name")}
              className={FIELD_CLASS}
              {...register("product_name")}
            />
            {errors.product_name && (
              <p
                className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                data-testid="create-sku-bundle-product-name-error"
              >
                {errors.product_name.message}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]">
              {t("modal.create_sku_bundle.field_code")}
            </label>
            <input
              type="text"
              data-testid="create-sku-bundle-code"
              disabled={isPending}
              placeholder={t("modal.create_sku_bundle.placeholder_code")}
              className={`sku ${FIELD_CLASS}`}
              {...register("code")}
            />
            {errors.code && (
              <p
                className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                data-testid="create-sku-bundle-code-error"
              >
                {errors.code.message}
              </p>
            )}
          </div>

          {!isBundle && (
            <div>
              <label
                htmlFor="create-sku-ap-name"
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t("modal.create_sku_bundle.field_ap_name")}
              </label>
              {/* Native datalist: suggests existing spellings (TONG GARDEN, not TONGGARDEN) without a combobox lib. */}
              <input
                id="create-sku-ap-name"
                type="text"
                list="create-sku-ap-name-options"
                autoComplete="off"
                data-testid="create-sku-ap-name"
                disabled={isPending}
                placeholder={t("modal.create_sku_bundle.placeholder_ap_name")}
                className={FIELD_CLASS}
                {...register("ap_name")}
              />
              <datalist id="create-sku-ap-name-options" data-testid="create-sku-ap-name-options">
                {(apNames.data ?? []).map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>
          )}

          {!isBundle && (
            <p className="text-[12.5px] text-[var(--text-subtle)]">
              {t("modal.create_sku_bundle.hint")}
            </p>
          )}

          {isBundle && (
            <fieldset className="space-y-2.5">
              <legend className="mb-0.5 text-xs font-semibold text-[var(--text-secondary)]">
                {t("modal.create_sku_bundle.components_legend")}
              </legend>

              <BaseProductPicker
                disabled={isPending}
                excludeSkus={pickedSkus}
                onSelect={(item) =>
                  append({
                    base_sino_sku: item.sku,
                    product_name: item.product_name,
                    multiplicity: 1,
                  })
                }
              />

              {fields.length === 0 && (
                <p
                  className="text-[12.5px] text-[var(--text-subtle)]"
                  data-testid="create-bundle-components-hint"
                >
                  {t("modal.create_sku_bundle.components_empty_hint")}
                </p>
              )}

              <div className="space-y-2" data-testid="create-bundle-components">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 rounded-[var(--control-r)] border border-[var(--premium-border)] px-3 py-2"
                    data-testid="create-bundle-component-row"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
                        {field.product_name || field.base_sino_sku}
                      </p>
                      <p className="sku text-xs text-[var(--text-subtle)]">{field.base_sino_sku}</p>
                    </div>
                    <Controller
                      control={control}
                      name={`components.${index}.multiplicity`}
                      render={({ field: ctrl }) => (
                        <input
                          type="number"
                          min={1}
                          step={1}
                          data-testid={`bundle-component-multiplicity-${index}`}
                          disabled={isPending}
                          aria-label={t("modal.create_sku_bundle.multiplier")}
                          className={`${FIELD_CLASS.replace("w-full ", "")} w-20 text-right`}
                          value={ctrl.value == null ? "" : String(ctrl.value)}
                          onChange={(e) => ctrl.onChange(e.target.value)}
                          onBlur={ctrl.onBlur}
                        />
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      disabled={isPending}
                      aria-label={t("modal.create_sku_bundle.action_remove_component")}
                      data-testid={`bundle-component-remove-${index}`}
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] disabled:opacity-40"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>

              {fields.map((field, index) =>
                errors.components?.[index]?.multiplicity ? (
                  <p
                    key={`err-${field.id}`}
                    className="text-xs text-[var(--intent-danger-fg)]"
                    data-testid={`bundle-component-multiplicity-error-${index}`}
                  >
                    {errors.components[index]?.multiplicity?.message}
                  </p>
                ) : null,
              )}

              {typeof errors.components?.message === "string" && (
                <p
                  className="text-xs text-[var(--intent-danger-fg)]"
                  data-testid="create-bundle-components-error"
                >
                  {errors.components.message}
                </p>
              )}
            </fieldset>
          )}

          {serverError && (
            <p
              className="text-sm text-[var(--intent-danger-fg)]"
              data-testid="create-sku-bundle-server-error"
            >
              {apiErrorMessage(serverError)}
            </p>
          )}

          <div className="flex justify-end gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              data-testid="create-sku-bundle-cancel"
              className="h-10 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--canvas)] disabled:opacity-50"
            >
              {t("modal.create_sku_bundle.action_cancel")}
            </button>
            <button
              type="submit"
              disabled={isPending}
              data-testid="create-sku-bundle-submit"
              className="inline-flex h-10 items-center gap-1.5 rounded-[var(--control-r)] px-5 text-[13.5px] font-bold text-white transition hover:-translate-y-px disabled:opacity-50"
              style={{
                background: "var(--grad-primary-btn)",
                boxShadow:
                  "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5),inset 0 1px 0 rgba(255,255,255,.22)",
              }}
            >
              <Plus className="h-[15px] w-[15px]" aria-hidden />
              {isPending ? "…" : t("modal.create_sku_bundle.action_create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
